import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { SubscriptionService, SubscriptionData } from '../services/subscriptionService';
import { AdminTierService } from '../services/adminTierService';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { MemberPermissionsService, MemberPermissions } from '../services/memberPermissionsService';

export interface UseSubscriptionReturn {
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  hasAnalyticsAccess: boolean;
  tier: string;
  isProOrHigher: boolean;
  isBusinessOrHigher: boolean;
  isEnterprise: boolean;
  isAdminOverride: boolean; // New: indicates if tier is overridden
  memberPermissions: MemberPermissions | null; // Member permissions (if user is member)
  isMember: boolean; // Convenience flag
  blockedElements: string[]; // Elements blocked by admin policy
}

export const useSubscription = (): UseSubscriptionReturn => {
  // Add error handling for useAuth
  let authData: { userId: string | null | undefined; getToken: (() => Promise<string | null>) | null | undefined };
  try {
    authData = useAuth();
  } catch (error) {
    console.warn('useAuth failed, using fallback values:', error);
    authData = { userId: null, getToken: null };
  }

  const { userId: clerkUserId, getToken: getClerkToken } = authData;
  const { impersonationState, getAuthToken: getImpersonationToken } = useImpersonation();
  
  // Use impersonated user ID if impersonating, otherwise use Clerk user ID
  const userId = impersonationState.isImpersonating && impersonationState.impersonatedUser
    ? impersonationState.impersonatedUser.id
    : clerkUserId;
  
  // Use impersonation-aware token (returns impersonation token if impersonating, otherwise Clerk token)
  const getToken = async () => {
    return await getImpersonationToken();
  };
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [memberPermissions, setMemberPermissions] = useState<MemberPermissions | null>(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!userId || !getToken) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch both subscription and member permissions in parallel
        const [subscriptionData, memberPerms] = await Promise.all([
          SubscriptionService.getCurrentSubscription(userId, getToken),
          MemberPermissionsService.getMemberPermissions(userId, getToken)
        ]);
        
        console.log('🔍 useSubscription - Fetched from DB:', subscriptionData);
        console.log('🔍 useSubscription - Member permissions:', memberPerms);
        console.log('🔍 useSubscription - Impersonation state:', impersonationState);
        console.log('🔍 useSubscription - User ID being used:', userId);
        
        setSubscription(subscriptionData);
        setMemberPermissions(memberPerms);
      } catch (err) {
        console.error('❌ useSubscription - Error fetching subscription:', err);
        setError('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, refreshKey, impersonationState.isImpersonating]); // Refetch when impersonation state changes

  // Listen for admin tier changes
  useEffect(() => {
    const handleTierChange = () => {
      console.log('🔄 Admin tier changed, refreshing...');
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('admin-tier-changed', handleTierChange);
    return () => window.removeEventListener('admin-tier-changed', handleTierChange);
  }, []);
  
  // Clear member permissions cache when impersonation state changes
  useEffect(() => {
    if (userId) {
      MemberPermissionsService.clearCache(userId);
      console.log('🔄 Impersonation state changed, cleared cache for user:', userId);
    }
  }, [impersonationState.isImpersonating, userId]);

  // Check for admin tier override first
  // Note: this only affects the frontend display — backend API enforces real subscription tiers
  const adminOverrideTier = AdminTierService.getOverrideTier();
  const isAdminOverride = !!adminOverrideTier;
  
  // Determine effective tier:
  // 1. Admin override (if set)
  // 2. Organization tier (if user is a member)
  // 3. Personal subscription tier
  // 4. Free (default)
  let effectiveTier = 'free';
  
  if (adminOverrideTier) {
    effectiveTier = adminOverrideTier;
    console.log('🎯 useSubscription - Using admin override tier:', effectiveTier);
  } else if (memberPermissions?.isMember && memberPermissions.organizationTier) {
    // Members inherit organization tier (no personal subscription needed)
    effectiveTier = memberPermissions.organizationTier;
    console.log('🎯 useSubscription - Member detected, using organization tier:', effectiveTier, {
      isMember: memberPermissions.isMember,
      organizationTier: memberPermissions.organizationTier,
      role: memberPermissions.role,
      impersonating: impersonationState.isImpersonating
    });
  } else if (subscription?.tier) {
    effectiveTier = subscription.tier;
    console.log('🎯 useSubscription - Using personal subscription tier:', effectiveTier);
  } else {
    console.log('🎯 useSubscription - No tier found, defaulting to free', {
      hasMemberPermissions: !!memberPermissions,
      isMember: memberPermissions?.isMember,
      organizationTier: memberPermissions?.organizationTier,
      hasSubscription: !!subscription,
      subscriptionTier: subscription?.tier
    });
  }
  
  const tier = effectiveTier;
  
  // Log tier determination (only when values actually change to prevent spam)
  useEffect(() => {
    console.log('🎯 useSubscription - Final tier determination:', {
      tier,
      adminOverride: adminOverrideTier,
      personalSubscription: subscription?.tier,
      isMember: memberPermissions?.isMember,
      organizationTier: memberPermissions?.organizationTier,
      impersonating: impersonationState.isImpersonating
    });
    if (isAdminOverride) {
      console.log(`🎯 ADMIN MODE: Testing as "${tier}" tier (actual: ${subscription?.tier || 'free'})`);
    }
    if (impersonationState.isImpersonating) {
      console.log(`🎯 IMPERSONATION MODE: Viewing as "${tier}" tier for user ${userId}`);
    }
  }, [adminOverrideTier, subscription?.tier, tier, isAdminOverride, memberPermissions, impersonationState.isImpersonating, userId]);
  
  // Determine access levels based on effective tier
  const isProOrHigher = ['pro', 'business', 'enterprise'].includes(tier);
  const isBusinessOrHigher = ['business', 'enterprise'].includes(tier);
  const isEnterprise = tier === 'enterprise';
  
  // Analytics access: Starter gets basic analytics, Pro+ gets full dashboard
  const hasAnalyticsAccess = tier === 'starter' || isProOrHigher;

  return {
    subscription,
    loading,
    error,
    hasAnalyticsAccess,
    tier,
    isProOrHigher,
    isBusinessOrHigher,
    isEnterprise,
    isAdminOverride,
    memberPermissions,
    isMember: memberPermissions?.isMember || false,
    blockedElements: memberPermissions?.blockedElements || []
  };
}; 